// Library sort preference — the column (and direction) the library table is
// sorted by, persisted per device (pre-sync).
//
// A fresh device sorts by last edited, newest first (`DEFAULT_SORT` in
// searchModel.ts); a deliberate header click sticks on this device. Like the
// journal date mode (journalDateModePreference.ts), the rail and the theme,
// this is DEVICE display chrome (memory project_theme_outside_event_log): its
// OWN localStorage key, never a domain event. Only the impure host
// reads/writes; the parse/serialize pair is pure.
//
// Stored as `column:direction` ("edited:descending"). Parse, don't validate:
// an absent key or a value an older build wrote degrades gracefully — the
// register's browse orders map to their columns ("title" -> title ascending,
// "freshness" -> edited descending, "kind" -> kind ascending), anything else
// is "no preference" (`undefined`), never a crash.

import {
    type LibrarySort,
    SORT_COLUMNS,
    type SortDirection,
} from "../components/Search/searchModel.js";
import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

const STORAGE_KEY = "bilda.librarySort";

const DIRECTIONS: readonly SortDirection[] = ["ascending", "descending"];

// Values the A–Ö register (before D23) stored.
const LEGACY: ReadonlyMap<string, LibrarySort> = new Map([
    ["title", { column: "title", direction: "ascending" }],
    ["freshness", { column: "edited", direction: "descending" }],
    ["kind", { column: "kind", direction: "ascending" }],
]);

// --- Parsing (pure) ------------------------------------------------------------

export const parseLibrarySort = (
    raw: string | null,
): LibrarySort | undefined => {
    if (raw === null) return undefined;
    const legacy = LEGACY.get(raw);
    if (legacy !== undefined) return legacy;
    const [c, d] = raw.split(":");
    const column = SORT_COLUMNS.find((x) => x === c);
    const direction = DIRECTIONS.find((x) => x === d);
    return column === undefined || direction === undefined
        ? undefined
        : { column, direction };
};

export const serializeLibrarySort = (sort: LibrarySort): string =>
    `${sort.column}:${sort.direction}`;

// --- The impure seam (host-only, like journalDateModePreference.ts) ------------

export const readLibrarySort = (): LibrarySort | undefined =>
    parseLibrarySort(readDeviceKey(STORAGE_KEY));

export const writeLibrarySort = (sort: LibrarySort): void =>
    writeDeviceKey(STORAGE_KEY, serializeLibrarySort(sort));
